import { createFiltersQuery } from 'util/createFiltersQuery';
import {
  selectLanguage,
  selectModeType,
  selectSorting,
  selectSummaryCategoryViewMode,
} from 'util/selector/querySelector';

import { toggleDepartment } from 'reduxStore/departments/asyncActions';
import {
  selectCanApplyFilter,
  selectIsDemographicSelected,
  selectIsFilterSelected,
  selectSelectedDemographics,
} from 'reduxStore/filtering/selectors';
import { Filtering } from 'model/Filtering';
import {
  BasicDemographyClassification,
  DemographyClassification,
} from 'model/DemographyClassification';
import {
  selectSelectedDepartmentIds,
  selectSelectedDepartments,
} from 'reduxStore/departments/selectors';
import { selectSelectedClassifications } from 'reduxStore/demography/selectors';
import { pushQuery } from 'reduxStore/router/asyncActions';
import { ModeType } from 'register/ModeType';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { serializeError } from 'shared/utils/redux';
import { Demographic } from 'model/Demography';

import { STORE_NAME } from './initialState';

export const toggleClassification = createAsyncThunk(
  `${STORE_NAME}/toggleClassification`,
  ({ classification }: { classification: DemographyClassification }, { getState }) => {
    let selectedClassifications = selectSelectedDemographics(getState());
    const isSelectedDemographic = selectIsDemographicSelected(classification)(getState());

    const updatedSelectedClassifications: BasicDemographyClassification[] = isSelectedDemographic
      ? (selectedClassifications = selectedClassifications.filter(
          (selectedClassification: BasicDemographyClassification) =>
            !DemographyClassification.hasEqualIds(classification, selectedClassification)
        ))
      : [
          ...selectedClassifications,
          { id: classification.id, demographyId: classification.demographyId },
        ];

    updatedSelectedClassifications.sort((a, b) => a.id - b.id || a.demographyId - b.demographyId);

    return updatedSelectedClassifications;
  }
);

export const changeFilter = createAsyncThunk<void, Filtering.ChangeFilteringCommand>(
  `${STORE_NAME}/changeFilter`,
  (command, { dispatch, getState }) => {
    const isSelected = selectIsFilterSelected(command)(getState());
    /**
     * Unselecting a filter is always allowed
     */
    if (isSelected) {
      switch (command.type) {
        case 'DEPARTMENT':
          dispatch(toggleDepartment({ departmentId: command.departmentId }));
          return;
        case 'DEMOGRAPHY':
          dispatch(toggleClassification({ classification: command.classification }));
          return;
      }
    }

    const canSelectFilter = selectCanApplyFilter(command)(getState());

    if (canSelectFilter) {
      switch (command.type) {
        case 'DEPARTMENT':
          dispatch(toggleDepartment({ departmentId: command.departmentId }));
          break;
        case 'DEMOGRAPHY':
          dispatch(toggleClassification({ classification: command.classification }));
          break;
      }
    }
  }
);

export const setMode = createAsyncThunk<void, ModeType>(
  `${STORE_NAME}/setMode`,
  (summaryMode, { getState, dispatch }) => {
    const selectedClassifications = selectSelectedClassifications(getState());
    const selectedDepartmentsIds = selectSelectedDepartmentIds(getState());
    const categoryMode = selectSummaryCategoryViewMode(getState());
    const sortBy = selectSorting(getState());
    const language = selectLanguage(getState());

    dispatch(
      pushQuery({
        query: createFiltersQuery({
          classifications: selectedClassifications,
          departments: selectedDepartmentsIds,
          summaryMode,
          categoryMode,
          sortBy,
          language,
        }),
      })
    );
  },
  { serializeError }
);

export const applyAllFilters = createAsyncThunk<void>(
  `${STORE_NAME}/applyAllFilters`,
  (_, { dispatch, getState }) => {
    const selectedDepartmentsIds = selectSelectedDepartments(getState());
    const selectedClassifications = selectSelectedDemographics(getState());
    const summaryMode = selectModeType(getState());
    const categoryMode = selectSummaryCategoryViewMode(getState());
    const sortBy = selectSorting(getState());
    const language = selectLanguage(getState());

    dispatch(
      pushQuery({
        query: createFiltersQuery({
          classifications: selectedClassifications,
          departments: selectedDepartmentsIds,
          summaryMode,
          categoryMode,
          sortBy,
          language,
        }),
      })
    );
  },
  { serializeError }
);

const arraysHaveSameClassifications = (
  arr1: BasicDemographyClassification[],
  arr2: BasicDemographyClassification[]
): boolean => {
  if (arr1.length !== arr2.length) return false;

  const sorted1 = arr1.slice().sort((a, b) => a.demographyId - b.demographyId || a.id - b.id);
  const sorted2 = arr2.slice().sort((a, b) => a.demographyId - b.demographyId || a.id - b.id);

  return sorted1.every((item1, index) =>
    DemographyClassification.hasEqualIds(item1, sorted2[index])
  );
};

export const selectInitialDemographics = createAsyncThunk(
  `${STORE_NAME}/selectInitialDemographics`,
  (_, { getState }) => {
    const selectedClassifications = selectSelectedClassifications(getState());
    const gotSelectedClassifications = selectedClassifications?.length > 0;
    const selectedLocalClassifications = selectSelectedDemographics(getState());
    const gotSelectedLocalClassifications = selectedLocalClassifications?.length > 0;

    let currentClassifications: BasicDemographyClassification[] = [];

    if (gotSelectedClassifications && !gotSelectedLocalClassifications) {
      currentClassifications = selectedClassifications;
    } else if (
      arraysHaveSameClassifications(selectedClassifications, selectedLocalClassifications)
    ) {
      currentClassifications = selectedLocalClassifications;
    }

    return currentClassifications;
  }
);

export const selectParentDemographic = createAsyncThunk<
  BasicDemographyClassification[],
  { demographic: Demographic; select: boolean }
>(`${STORE_NAME}/selectParentDemographic`, ({ demographic, select }, { getState }) => {
  const selectedClassifications = selectSelectedDemographics(getState());

  const demographicChildren: BasicDemographyClassification[] = demographic.classifications.map(
    (classification) => ({
      id: classification.id,
      demographyId: classification.demographyId,
    })
  );

  if (select) {
    const existingSet = new Set(
      selectedClassifications.map((item) => `${item.id}-${item.demographyId}`)
    );
    const newChildren = demographicChildren.filter(
      (child) => !existingSet.has(`${child.id}-${child.demographyId}`)
    );
    return [...selectedClassifications, ...newChildren];
  }

  const childrenSet = new Set(
    demographicChildren.map((child) => `${child.id}-${child.demographyId}`)
  );
  return selectedClassifications.filter(
    (selected) => !childrenSet.has(`${selected.id}-${selected.demographyId}`)
  );
});
